import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeStyle as _normalizeStyle, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "grow" }
const _hoisted_2 = { class: "my-2 line-clamp-3" }
const _hoisted_3 = {
  key: 0,
  class: "pl-2"
}
const _hoisted_4 = { class: "flex flex-wrap items-center" }

import { ref, computed } from "vue";

import { getClasses } from "~/js/composables/classes";
import { getTranslation, getGlobalTranslation } from "~/js/composables/translations";
import { getTypeColors } from "~/js/composables/colors";
import { getHtmlSpecialCharsDecode } from "~/js/composables/parse-html";

import { Translations } from "~/js/interfaces/translations";
import { KnowledgeHubCard } from "~/js/interfaces/knowledge-hub";
import { Analytics } from "~/js/interfaces/Analytics";

import Badge from "~/vue/atoms/badges/BadgePrimary.vue";
import ImageTeaser from "~/vue/atoms/images/ImageTeaser.vue";
import MetaPillars from "~/vue/molecules/metas/MetaPillars.vue";
import TextH4 from "~/vue/atoms/texts/TextH4.vue";
import LinkOverlay from "~/vue/atoms/links/LinkOverlay.vue";
import ButtonTertiary from "~/vue/atoms/buttons/ButtonTertiary.vue";

interface Props {
	card: KnowledgeHubCard;
	translations?: Translations | null;
	showTeaser?: boolean;
	isFeatured?: boolean;
	utilities?: string;
	hideTags?: boolean;
	background?: string;
	ga?: Analytics | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CardPress',
  props: {
    card: {},
    translations: { default: null },
    showTeaser: { type: Boolean, default: true },
    isFeatured: { type: Boolean, default: false },
    utilities: { default: "" },
    hideTags: { type: Boolean, default: false },
    background: { default: "bg-white" },
    ga: { default: null }
  },
  setup(__props: any) {

const props = __props;

const visibleTeaser = computed(() => {
	return props.card?.teaser && props.showTeaser;
});

const colors = ref(getTypeColors());
const classes = ref({
	badge: {
		position: visibleTeaser.value ? "absolute left-4 top-4 z-10" : "",
		margin: !visibleTeaser.value ? "inline-block mb-4" : "",
	},
	description: {
		color: "text-concrete-700",
		clamp: "line-clamp-4",
		font: "font-primary text-sm",
		lineHeight: "leading-relaxed",
		margin: "pt-3",
	},
	footer: {
		margin: "mt-3",
		flex: "flex justify-between items-end",
	},
	meta: {
		color: "text-concrete-700",
		font: "font-primary text-sm font-bold",
		lineHeight: "leading-relaxed",
		padding: "space-x-2",
	},
	header: {
		color: "bg-gray-200",
		display: "rounded-2xl overflow-hidden",
		position: "relative",
		ratio: `${props.isFeatured ? "lg:aspect-4/3 xl:aspect-video" : ""} aspect-4/3`,
		size: `${props.isFeatured ? "lg:w-1/2" : ""} w-full`,
		margin: props.isFeatured ? "" : "mb-4",
	},
	image: {
		position: "absolute left-0 top-0",
		size: "w-full h-full",
		object: "object-cover object-center",
	},
	contentWrapper: {
		flex: "flex flex-col justify-between grow",
		padding: `${props.isFeatured || !visibleTeaser.value ? "py-4" : "pb-4"} px-4`,
		size: `${props.isFeatured ? "lg:w-1/2" : ""} w-full`,
	},
	wrapper: {
		animation: "transition-all duration-700",
		background: props.background,
		display: "rounded-3xl cursor-pointer relative",
		flex: props.isFeatured ? "flex gap-2" : "flex flex-col",
		focus: `focus:shadow-2xl`,
		hover: `hover:shadow-2xl ${colors.value["pressRelease"].hover}`,
		padding: "p-2",
		utilities: props.utilities,
	},
});

const stripInfo = (meta: string) => {
	let stripped = meta;

	// press release strips
	stripped = stripped.replace(" 00:00", "");

	return stripped;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("article", {
    class: _normalizeClass(_unref(getClasses)(classes.value, 'wrapper'))
  }, [
    (visibleTeaser.value)
      ? (_openBlock(), _createElementBlock("header", {
          key: 0,
          class: _normalizeClass(_unref(getClasses)(classes.value, 'header'))
        }, [
          _createVNode(Badge, {
            content: _unref(getGlobalTranslation)('Press Release'),
            style: _normalizeStyle(colors.value['pressRelease'].badge),
            utilities: _unref(getClasses)(classes.value, 'badge')
          }, null, 8, ["content", "style", "utilities"]),
          (__props.card.teaser)
            ? (_openBlock(), _createBlock(ImageTeaser, {
                key: 0,
                utilities: "h-full w-full",
                image: __props.card.teaser,
                sizes: [
					'(max-width: 640px) 100vw',
					'(min-width: 640px and max-width: 768px) 50vw',
					'(min-width: 768px and max-width: 1024px) 33vw',
					'(min-width: 1024px) 25vw',
				]
              }, null, 8, ["image"]))
            : _createCommentVNode("", true)
        ], 2))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass(_unref(getClasses)(classes.value, 'contentWrapper'))
    }, [
      _createElementVNode("div", _hoisted_1, [
        (!visibleTeaser.value)
          ? (_openBlock(), _createBlock(Badge, {
              key: 0,
              content: _unref(getGlobalTranslation)('Press Release'),
              style: _normalizeStyle(colors.value['pressRelease'].badge),
              utilities: _unref(getClasses)(classes.value, 'badge')
            }, null, 8, ["content", "style", "utilities"]))
          : _createCommentVNode("", true),
        (__props.card?.pillarsFull)
          ? (_openBlock(), _createBlock(MetaPillars, {
              key: 1,
              content: __props.card.pillarsFull
            }, null, 8, ["content"]))
          : _createCommentVNode("", true),
        _createElementVNode("h1", _hoisted_2, [
          _createVNode(TextH4, null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(getHtmlSpecialCharsDecode)(__props.card.title)), 1)
            ]),
            _: 1
          })
        ]),
        _createElementVNode("p", {
          class: _normalizeClass(_unref(getClasses)(classes.value, 'meta'))
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.card?.meta, (meta, key) => {
            return (_openBlock(), _createElementBlock("span", { key: meta }, [
              _createTextVNode(_toDisplayString(stripInfo(meta)), 1),
              (key < __props.card.meta.length - 1)
                ? (_openBlock(), _createElementBlock("span", _hoisted_3, "•"))
                : _createCommentVNode("", true)
            ]))
          }), 128))
        ], 2),
        (__props.card?.description)
          ? (_openBlock(), _createElementBlock("p", {
              key: 2,
              class: _normalizeClass(_unref(getClasses)(classes.value, 'description'))
            }, _toDisplayString(__props.card.description), 3))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("footer", {
        class: _normalizeClass(_unref(getClasses)(classes.value, 'footer'))
      }, [
        _createElementVNode("div", _hoisted_4, [
          (__props.card.regulatedInformation && !__props.hideTags)
            ? (_openBlock(), _createBlock(Badge, {
                key: 0,
                content: _unref(getGlobalTranslation)('Regulated information'),
                style: _normalizeStyle(colors.value['pressRelease'].badge),
                size: "small",
                utilities: "mr-1 mt-2"
              }, null, 8, ["content", "style"]))
            : _createCommentVNode("", true),
          (__props.card.insideInformation && !__props.hideTags)
            ? (_openBlock(), _createBlock(Badge, {
                key: 1,
                content: _unref(getGlobalTranslation)('Inside information'),
                style: _normalizeStyle(colors.value['pressRelease'].badge),
                size: "small",
                utilities: "mr-1 mt-2"
              }, null, 8, ["content", "style"]))
            : _createCommentVNode("", true)
        ]),
        _createVNode(ButtonTertiary, {
          size: "small",
          icon: "arrow-right",
          type: "div",
          utilities: "inline-flex mb-1.5 ml-2"
        })
      ], 2)
    ], 2),
    _createVNode(LinkOverlay, {
      title: __props.card.title,
      url: __props.card.url,
      ga: 
				__props.ga ?? {
					category: 'card',
					action: 'click',
					label: __props.card.title,
				}
			
    }, null, 8, ["title", "url", "ga"])
  ], 2))
}
}

})